import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowUpDown, ArrowUp, ArrowDown, Trash2, Plus, RotateCcw, Loader2, Pencil, Check, X, GripVertical } from "lucide-react";

const MODULES = [
  { key: "rivenditori", label: "Rivenditori" },
  { key: "ambassador", label: "Ambassador" },
  { key: "redazionali", label: "Media & PR" },
];
const DIRS = { asc: "Crescente (A-Z / dal più basso / dal meno recente)", desc: "Decrescente (Z-A / dal più alto / dal più recente)" };

/* Editor dell'ordine dei valori per i campi enumerati (Stato, Categoria, Tipologia). */
function ValuesOrderEditor({ values, allValues, labels, onChange, testidPrefix }) {
  const [extra, setExtra] = useState("");
  const move = (i, d) => { const a = [...values]; const j = i + d; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; onChange(a); };
  const missing = (allValues || []).filter((v) => !values.includes(v));
  return (
    <div className="space-y-1.5" data-testid={`${testidPrefix}-values-editor`}>
      <p className="text-[11px] text-muted-foreground">Ordine dei valori (dal primo all'ultimo):</p>
      <div className="flex flex-wrap gap-1.5">
        {values.map((v, i) => (
          <span key={v} className="inline-flex items-center gap-0.5 rounded-full border border-border bg-brema-bg px-2 py-0.5 text-[11px] text-foreground" data-testid={`${testidPrefix}-value-chip`}>
            <span className="font-semibold">{i + 1}.</span> {labels?.[v] || v}
            <button type="button" onClick={() => move(i, -1)} className="ml-1 text-muted-foreground hover:text-foreground" title="Sposta prima"><ArrowUp className="h-3 w-3" /></button>
            <button type="button" onClick={() => move(i, 1)} className="text-muted-foreground hover:text-foreground" title="Sposta dopo"><ArrowDown className="h-3 w-3" /></button>
            <button type="button" onClick={() => onChange(values.filter((x) => x !== v))} className="text-muted-foreground hover:text-danger" title="Rimuovi"><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {missing.map((v) => (
          <button key={v} type="button" onClick={() => onChange([...values, v])} data-testid={`${testidPrefix}-add-value`}
            className="rounded-full border border-dashed border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:border-brand hover:text-brand">+ {labels?.[v] || v}</button>
        ))}
        <Input value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="Altro valore…" className="h-7 w-36 text-xs"
          data-testid={`${testidPrefix}-extra-input`}
          onKeyDown={(e) => { if (e.key === "Enter" && extra.trim()) { onChange([...values, extra.trim()]); setExtra(""); } }} />
      </div>
    </div>
  );
}

function OptionForm({ draft, setDraft, catalog, onSave, onCancel, saving, testidPrefix }) {
  const meta = catalog?.[draft.field] || {};
  return (
    <div className="space-y-3 rounded-xl border border-brand/40 bg-brema-bg/40 p-3" data-testid={`${testidPrefix}-form`}>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1">
          <Label className="text-xs">Etichetta nel menu</Label>
          <Input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} className="h-9 text-sm" placeholder="Es. Follower · dal più alto" data-testid={`${testidPrefix}-label-input`} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Campo</Label>
          <Select value={draft.field} onValueChange={(v) => setDraft({ ...draft, field: v, values_order: catalog?.[v]?.type === "enum" ? (catalog[v].values || []) : [] })}>
            <SelectTrigger className="h-9 text-sm" data-testid={`${testidPrefix}-field-select`}><SelectValue placeholder="Scegli campo" /></SelectTrigger>
            <SelectContent>
              {Object.entries(catalog || {}).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Direzione</Label>
          <Select value={draft.dir} onValueChange={(v) => setDraft({ ...draft, dir: v })}>
            <SelectTrigger className="h-9 text-sm" data-testid={`${testidPrefix}-dir-select`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="asc">{DIRS.asc}</SelectItem>
              <SelectItem value="desc">{DIRS.desc}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {meta.type === "enum" && (
        <ValuesOrderEditor values={draft.values_order || []} allValues={meta.values} labels={meta.value_labels}
          onChange={(vals) => setDraft({ ...draft, values_order: vals })} testidPrefix={testidPrefix} />
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" size="sm" onClick={onCancel} className="rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover" data-testid={`${testidPrefix}-cancel`}>Annulla</Button>
        <Button type="button" size="sm" onClick={onSave} disabled={saving || !draft.label.trim() || !draft.field} className="rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90" data-testid={`${testidPrefix}-save`}>
          {saving ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1 h-3.5 w-3.5" />} Salva voce
        </Button>
      </div>
    </div>
  );
}

const emptyDraft = { label: "", field: "", dir: "desc", values_order: [] };

export function SortOptionsEditor() {
  const [module, setModule] = useState("rivenditori");
  const [options, setOptions] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | option id
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/sort-options?module=${module}`)
      .then(({ data }) => { setOptions(data.options || []); setCatalog(data.catalog || {}); })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Caricamento fallito"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { setEditing(null); load(); }, [module]); // eslint-disable-line react-hooks/exhaustive-deps

  const startNew = () => { setDraft(emptyDraft); setEditing("new"); };
  const startEdit = (o) => { setDraft({ label: o.label, field: o.field, dir: o.dir, values_order: o.values_order || [] }); setEditing(o.id); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") await api.post("/admin/sort-options", { module, ...draft });
      else await api.patch(`/admin/sort-options/${editing}`, draft);
      toast.success("Voce di ordinamento salvata");
      setEditing(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Salvataggio fallito"); }
    finally { setSaving(false); }
  };
  const remove = async (o) => {
    if (!window.confirm(`Eliminare la voce "${o.label}" dal menu Ordina per?`)) return;
    try { await api.delete(`/admin/sort-options/${o.id}`); toast.success("Voce eliminata"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Eliminazione fallita"); }
  };
  const move = async (i, d) => {
    const arr = [...options]; const j = i + d; if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]]; setOptions(arr);
    try { await api.post("/admin/sort-options/reorder", { ids: arr.map((x) => x.id) }); } catch { load(); }
  };
  const reset = async () => {
    if (!window.confirm("Ripristinare le voci predefinite per questo modulo? Le personalizzazioni andranno perse.")) return;
    try { await api.post("/admin/sort-options/reset", { module }); toast.success("Voci predefinite ripristinate"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Ripristino fallito"); }
  };

  const dirLabel = (o) => {
    const t = catalog?.[o.field]?.type;
    if (t === "enum") return "ordine personalizzato";
    if (t === "date") return o.dir === "desc" ? "dal più recente" : "dal meno recente";
    if (t === "number") return o.dir === "desc" ? "dal più alto" : "dal più basso";
    return o.dir === "desc" ? "Z-A" : "A-Z";
  };

  return (
    <div className="rounded-2xl border border-border bg-brema-card p-5" data-testid="sort-options-editor">
      <div className="mb-2 flex items-center gap-2 text-foreground">
        <ArrowUpDown className="h-5 w-5 text-brand" />
        <h3 className="font-semibold">Ordinamenti Finder ("Ordina per")</h3>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        Voci del menu "Ordina per" nelle viste card e tabella di Rivenditori, Ambassador e Media & PR. Aggiungi, rinomina, riordina o elimina le voci; per Stato, Categoria e Tipologia puoi definire l'ordine dei valori.
      </p>
      <Tabs value={module} onValueChange={setModule}>
        <TabsList className="mb-4 bg-brema-bg">
          {MODULES.map((m) => <TabsTrigger key={m.key} value={m.key} data-testid={`sort-module-${m.key}`}>{m.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      {loading ? (
        <div className="flex items-center gap-2 py-6 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>
      ) : (
        <div className="space-y-2">
          {options.map((o, i) => (
            <div key={o.id} data-testid="sort-option-row">
              {editing === o.id ? (
                <OptionForm draft={draft} setDraft={setDraft} catalog={catalog} onSave={save} onCancel={() => setEditing(null)} saving={saving} testidPrefix="sort-edit" />
              ) : (
                <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-brema-bg/40 px-3 py-2">
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground" data-testid="sort-option-label">{o.label}</p>
                    <p className="text-[11px] text-muted-foreground">{catalog?.[o.field]?.label || o.field} · {dirLabel(o)}{o.values_order?.length ? ` · ${o.values_order.map((v) => catalog?.[o.field]?.value_labels?.[v] || v).join(" → ")}` : ""}</p>
                  </div>
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="rounded-full p-1 text-muted-foreground hover:bg-brema-hover hover:text-foreground disabled:opacity-30" data-testid="sort-option-up" title="Sposta su"><ArrowUp className="h-4 w-4" /></button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === options.length - 1} className="rounded-full p-1 text-muted-foreground hover:bg-brema-hover hover:text-foreground disabled:opacity-30" data-testid="sort-option-down" title="Sposta giù"><ArrowDown className="h-4 w-4" /></button>
                  <Button type="button" variant="secondary" size="sm" onClick={() => startEdit(o)} className="h-8 rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover" data-testid="sort-option-edit"><Pencil className="mr-1 h-3.5 w-3.5" /> Modifica</Button>
                  <Button type="button" variant="danger" size="sm" onClick={() => remove(o)} className="h-8 rounded-full text-xs" data-testid="sort-option-delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              )}
            </div>
          ))}
          {editing === "new" ? (
            <OptionForm draft={draft} setDraft={setDraft} catalog={catalog} onSave={save} onCancel={() => setEditing(null)} saving={saving} testidPrefix="sort-new" />
          ) : (
            <div className="flex flex-wrap items-center gap-2 pt-2">
              <Button type="button" size="sm" onClick={startNew} className="rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90" data-testid="sort-option-add-btn"><Plus className="mr-1 h-3.5 w-3.5" /> Aggiungi voce</Button>
              <Button type="button" variant="secondary" size="sm" onClick={reset} className="rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover" data-testid="sort-option-reset-btn"><RotateCcw className="mr-1 h-3.5 w-3.5" /> Ripristina predefinite</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
